import React, { createContext, useState, useContext, ReactNode, useEffect, useMemo, useCallback } from 'react';
import BreadcrumbGroup, { BreadcrumbGroupProps } from '@cloudscape-design/components/breadcrumb-group';
import { useLocation } from 'react-router-dom';


export interface Breadcrumb {
  text: string;
  href: string;
  index: number;
}

interface BreadcrumbContextType {
  breadcrumbs: Breadcrumb[];
  setActiveBreadcrumb: (newBreadcrumb: Breadcrumb, maxIndex?: number) => void;
  resetBreadcrumbs: () => void;
}

const BreadcrumbContext = createContext<BreadcrumbContextType | undefined>(undefined);

interface BreadcrumbProviderProps {
  children: ReactNode;
}

export function BreadcrumbProvider({ children }: BreadcrumbProviderProps): JSX.Element {
  const isFirstMount = React.useRef(true);

  useEffect(() => {
    console.log('MyContextProvider mounted');
    
    if (isFirstMount.current) {
      localStorage.removeItem('breadcrumbs');
      isFirstMount.current = false;
    }
    
    return () => {
      console.log('MyContextProvider unmounted');
    };
  }, []);

  const [breadcrumbs, setBreadcrumbs] = useState<Breadcrumb[]>(() => {
    try {
      const savedBreadcrumbs = localStorage.getItem('breadcrumbs');
      return savedBreadcrumbs 
        ? JSON.parse(savedBreadcrumbs) 
        : [{ text: 'Home', href: '/', index: 0 }];
    } catch (error) {
      console.error('Error loading breadcrumbs from localStorage:', error);
      return [{ text: 'Home', href: '/', index: 0 }];
    }
  });
  
  useEffect(() => {
    try {
      const currentSaved = localStorage.getItem('breadcrumbs');
      const newValue = JSON.stringify(breadcrumbs);
      if (currentSaved !== newValue) {
        localStorage.setItem('breadcrumbs', newValue);
      }
    } catch (error) {
      console.error('Error saving breadcrumbs to localStorage:', error);
    }
  }, [breadcrumbs]);
  
  const setActiveBreadcrumb = useCallback((newBreadcrumb: Breadcrumb, maxIndex: number = Infinity): void => {
    if (!newBreadcrumb || !newBreadcrumb.text || !newBreadcrumb.href || typeof newBreadcrumb.index !== 'number') {
      console.error('Invalid breadcrumb format. Each breadcrumb needs text, href, and index properties.');
      return;
    }
    
    setBreadcrumbs(prevBreadcrumbs => {
      const filteredBreadcrumbs = prevBreadcrumbs
        .filter(crumb => crumb.index !== newBreadcrumb.index)
        .filter(crumb => crumb.index < maxIndex);
      
      return [...filteredBreadcrumbs, newBreadcrumb]
        .sort((a, b) => a.index - b.index);
    });
  }, []);
  
  const resetBreadcrumbs = useCallback((): void => {
    setBreadcrumbs([{ text: 'Home', href: '/', index: 0 }]);
  }, []);

  const contextValue = useMemo(() => ({ 
    breadcrumbs, 
    setActiveBreadcrumb,
    resetBreadcrumbs
  }), [breadcrumbs, setActiveBreadcrumb, resetBreadcrumbs]);

  return (
    <BreadcrumbContext.Provider value={contextValue}>
      {children}
    </BreadcrumbContext.Provider>
  );
}

export function useBreadcrumbs(): BreadcrumbContextType {
  const context = useContext(BreadcrumbContext);
  if (!context) {
    throw new Error('useBreadcrumbs must be used within a BreadcrumbProvider');
  }
  return context;
}


export default function Breadcrumbs(): JSX.Element | null {
  const { breadcrumbs } = useBreadcrumbs();
  const location = useLocation();
  
  const breadcrumbItems = useMemo(
    () => breadcrumbs.map(({ text, href }) => ({ text, href })),
    [breadcrumbs]
  );
  
  if (location.pathname === '/authentiate') {
    return null;
  }
  
  return <BreadcrumbGroup items={breadcrumbItems} />;
}
